import { useQuery } from "@tanstack/react-query";
import { useUserSettings } from "@/hooks/useUserSettings";
import { formatCurrencyAmount } from "@/lib/currency";
import { repositoryFactory } from "@/repositories/RepositoryFactory";
import { type FxRate } from "@/types/fx";

export interface AdvancedCurrencyDisplay {
  amount: number;
  formattedAmount: string;
  originalAmount: number;
  originalCurrency: string;
  targetCurrency: string;
  isConverted: boolean;
  rate: number | null;
  rateDate: Date | null;
  rateSource?: string;
  confidence?: number;
}

/**
 * ADR-008: Advanced currency display hook
 * - Uses historical rate if transactionDate is provided, otherwise current rate.
 * - Falls back gracefully when currencies match.
 */
export function useAdvancedCurrencyDisplay(
  amount: number,
  originalCurrency: string,
  transactionDate?: Date,
  options?: {
    displayCurrency?: string;
    staleTimeMs?: number;
    cacheTimeMs?: number;
  },
) {
  const { userSettings } = useUserSettings();
  const displayCurrency =
    options?.displayCurrency || userSettings?.currency || "EUR";
  const staleTime = options?.staleTimeMs ?? 1000 * 60 * 60; // 1 hour
  const cacheTime = options?.cacheTimeMs ?? 1000 * 60 * 60 * 24; // 24 hours

  return useQuery<AdvancedCurrencyDisplay>({
    queryKey: [
      "currency-display",
      amount,
      originalCurrency,
      displayCurrency,
      transactionDate ? new Date(transactionDate).toDateString() : "current",
    ],
    queryFn: async () => {
      if (originalCurrency === displayCurrency) {
        return {
          amount,
          formattedAmount: formatCurrencyAmount(amount, displayCurrency),
          originalAmount: amount,
          originalCurrency,
          targetCurrency: displayCurrency,
          isConverted: false,
          rate: null,
          rateDate: null,
        };
      }

      const fxManager = repositoryFactory.getFxRateManager();
      let rate: FxRate;
      if (transactionDate) {
        rate = await fxManager.getHistoricalRate(
          originalCurrency,
          displayCurrency,
          transactionDate,
        );
      } else {
        rate = await fxManager.getCurrentRate(
          originalCurrency,
          displayCurrency,
        );
      }

      const convertedAmount = amount * rate.rate;

      return {
        amount: convertedAmount,
        formattedAmount: formatCurrencyAmount(convertedAmount, displayCurrency),
        originalAmount: amount,
        originalCurrency,
        targetCurrency: displayCurrency,
        isConverted: true,
        rate: rate.rate,
        rateDate: rate.date,
        rateSource: rate.source,
        confidence: rate.confidence,
      };
    },
    staleTime,
    gcTime: cacheTime,
  });
}
